import { apiClient } from '../utils/api-client';
import { throwForResponse } from './api-error';
import type { Stage, StageInput, StageResponse, StagesListResponse } from '../types/trophy';

/** What a gym asks for when it makes an event: a name and a weekend. */
export type EventInput = Omit<StageInput, 'venue_id'>;

/**
 * The competitions held at a gym — its own events and the stages it hosts —
 * as the gym's panel makes and unmakes them. Same card as a trophy's stages
 * (`Stage`), because it is the same record seen from the venue's side.
 */
class EventsService {
  async list(gymSlug: string): Promise<Stage[]> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/events`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the events');
    }

    const data: StagesListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  async create(gymSlug: string, attributes: EventInput): Promise<Stage> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/events`, {
      data: { attributes },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to create the event');
    }

    const data: StageResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Refused once anyone has entered it (`stage_has_participants`), and for a
   * stage the gym merely hosts (`stage_belongs_to_trophy`) — that one is the
   * circuit's to delete.
   */
  async delete(gymSlug: string, eventId: string): Promise<void> {
    const response = await apiClient.delete(`/api/gyms/${gymSlug}/events/${eventId}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete the event');
    }
  }
}

export const eventsService = new EventsService();
